const domain = process.env.REACT_APP_SHOPIFY_DOMAIN;
const storefrontAccessToken = process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN;
const STOREFRONT_API_VERSION = "2025-07";

const gql = async (query, variables = {}) => {
  const endpoint = `https://${domain}/api/${STOREFRONT_API_VERSION}/graphql.json`;
  const res = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-Shopify-Storefront-Access-Token": storefrontAccessToken,
    },
    body: JSON.stringify({ query, variables }),
  });
  const payload = await res.json();
  if (!res.ok || payload?.errors) {
    const msg = payload?.errors?.[0]?.message || `HTTP ${res.status}`;
    throw new Error(`Shopify API error: ${msg}`);
  }
  return payload.data;
};

const fetchAllProducts = async () => {
  if (!domain || !storefrontAccessToken) return [];
  const data = await gql(`{
    products(first: 50) {
      edges {
        node {
          title handle
          variants(first: 10) {
            edges { node { id title availableForSale price { amount } compareAtPrice { amount } image { url } selectedOptions { name value } } }
          }
          images(first: 5) { edges { node { url } } }
        }
      }
    }
  }`);
  return (data?.products?.edges || []).map((e) => e.node);
};

let cachedVeyro = null;

/** Auto-detect the Veyro backpack product(s) — aggregates color variants across all matching Shopify products. */
export const fetchVeyroProduct = async () => {
  if (cachedVeyro) return cachedVeyro;
  const nodes = await fetchAllProducts();
  const matches = nodes.filter((p) => /veyro|vacuum|backpack|compression/i.test(p.title));
  if (matches.length === 0) return null;
  const variants = matches.flatMap((p) =>
    p.variants.edges
      .map((e) => e.node)
      .filter((v) => v.availableForSale && parseFloat(v.price?.amount) > 0)
      .map((v) => {
        const compareAt = v.compareAtPrice ? parseFloat(v.compareAtPrice.amount) : null;
        return {
          id: v.id,
          title: v.title,
          price: parseFloat(v.price.amount),
          compareAt: compareAt > 0 ? compareAt : null,
          image: v.image?.url || p.images.edges[0]?.node?.url || null,
          options: (v.selectedOptions || []).map((o) => o.value).join(" "),
        };
      })
  );
  const variant = variants[0];
  if (!variant) return null;
  cachedVeyro = {
    title: matches[0].title,
    variants,
    variantId: variant.id,
    price: variant.price,
    compareAt: variant.compareAt,
    image: variant.image,
  };
  return cachedVeyro;
};

let cachedProtection = null;

/** Shipping Protection upsell (already published on the store). */
export const fetchShippingProtection = async () => {
  if (cachedProtection) return cachedProtection;
  const nodes = await fetchAllProducts();
  const match = nodes.find((p) => /shipping protection/i.test(p.title));
  if (!match) return null;
  const variant = match.variants.edges.map((e) => e.node).find((v) => v.availableForSale);
  if (!variant) return null;
  cachedProtection = {
    title: match.title,
    variantId: variant.id,
    price: parseFloat(variant.price.amount),
    image: variant.image?.url || match.images.edges[0]?.node?.url || null,
  };
  return cachedProtection;
};

/**
 * Create a Shopify cart via the Storefront Cart API and return its checkoutUrl.
 * lines: [{ merchandiseId, quantity }]
 */
export const createShopifyCheckout = async (lines) => {
  if (!domain || !storefrontAccessToken) {
    throw new Error("Shopify Storefront credentials are missing");
  }
  const data = await gql(
    `mutation CartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart { id checkoutUrl }
        userErrors { field message }
      }
    }`,
    { input: { lines } }
  );
  const userErrors = data?.cartCreate?.userErrors || [];
  if (userErrors.length > 0) {
    throw new Error(`Shopify cartCreate userError: ${userErrors[0].message}`);
  }
  const checkoutUrl = data?.cartCreate?.cart?.checkoutUrl;
  if (!checkoutUrl) throw new Error("Shopify cartCreate returned no checkoutUrl");
  return checkoutUrl;
};
